import React, {
  useState,
  useEffect,
  useRef
} from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import {
  useAuth
} from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  API_URL
} from '../../utils';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const ChatPage: React.FC = () => {
  const { language, t } = useLanguage();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState < Message[] > ([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const {
    idToken
  } = useAuth();
  const eventSourceRef = useRef < EventSource | null > (null);
  const messagesEndRef = useRef < null | HTMLDivElement > (null);
  const recognitionRef = useRef<any>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth"
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Detect Web Speech API support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsVoiceSupported(true);
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US'; // will be overridden on start
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.onresult = (event: any) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
      };
      recognition.onend = () => {
        setIsListening(false);
      };
      recognition.onerror = () => {
        setIsListening(false);
      };
      recognitionRef.current = recognition;
    } else {
      setIsVoiceSupported(false);
    }

    // Cleanup the event source on component unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
    };
  }, []);

  const handleSend = () => {
    if (input.trim() === '' || isLoading) return;

    const newMessages: Message[] = [...messages, {
      role: 'user',
      content: input
    }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    // Add a placeholder for the assistant's response
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: ''
    }]);

    const history = newMessages.slice(0, -1);
    const queryParams = new URLSearchParams({
      message: input,
      history: JSON.stringify(history),
      token: idToken || '',
    });

    const url = `${API_URL}/chat?${queryParams.toString()}`;
    eventSourceRef.current = new EventSource(url);

    eventSourceRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.event === 'done') {
        setIsLoading(false);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      } else if (data.chunk) {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            return [
              ...prev.slice(0, -1), {
                ...lastMessage,
                content: lastMessage.content + data.chunk
              },
            ];
          }
          return prev;
        });
      }
    };

    eventSourceRef.current.onerror = (err) => {
      console.error('EventSource failed:', err);
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          return [
            ...prev.slice(0, -1), {
              ...lastMessage,
              content: 'Sorry, I encountered an error. Please try again.'
            },
          ];
        }
        return prev;
      });
      setIsLoading(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  };

  const getSpeechLang = () => {
    // Map selected app language to a fixed speech recognition locale
    return language === 'es' ? 'es-ES' : 'en-US';
  };

  const handleToggleVoice = () => {
    if (!isVoiceSupported) return;
    const recognition = recognitionRef.current;
    if (!recognition) return;
    try {
      if (isListening) {
        recognition.stop();
        setIsListening(false);
      } else {
        recognition.lang = getSpeechLang();
        console.log('Speech recognition language:', recognition.lang);
        recognition.start();
        setIsListening(true);
      }
    } catch {
      setIsListening(false);
    }
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 120px)',
      display: 'flex',
      flexDirection: 'column',
      p: 2
    }}>
      <Typography variant="h4" gutterBottom>
        {t('chat.title')}
      </Typography>
      <Paper sx={{
        flexGrow: 1,
        p: 2,
        overflowY: 'auto',
        mb: 2
      }}>
        {messages.map((msg, index) => (
          <Box key={index} sx={{
            mb: 2,
            textAlign: msg.role === 'user' ? 'right' : 'left'
          }}>
            <Paper elevation={3} sx={{
              p: 1.5,
              display: 'inline-block',
              maxWidth: '70%',
              bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.300',
              color: msg.role === 'user' ? 'primary.contrastText' : 'black',
            }}>
              <Typography variant="body1">
                {msg.content}
              </Typography>
            </Paper>
          </Box>
        ))}
        {isLoading && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Paper>
      <Box sx={{
        display: 'flex'
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          disabled={isLoading}
        />
        <Button
          variant="outlined"
          color={isListening ? 'error' : 'secondary'}
          onClick={handleToggleVoice}
          disabled={!isVoiceSupported || isLoading}
          sx={{ ml: 2 }}
          title={!isVoiceSupported ? t('chat.voice.unsupported') : (isListening ? t('chat.voice.stop') : t('chat.voice.start'))}
          startIcon={isListening ? <MicOffIcon /> : <MicIcon />}
        >
          {isListening ? t('chat.voice.stop') : t('chat.voice.start')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={isLoading}
          sx={{
            ml: 2
          }}
        >
          {t('chat.send')}
        </Button>
      </Box>
      {isListening && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2">{t('chat.listening')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChatPage;
